/* eslint-disable react/require-default-props */
import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Badge, IconButton } from '@one-thd/sui-atomic-components';
import { ArrowForward } from '@one-thd/sui-icons';
import { FulfillmentPodShipping, FulfillmentPodStore } from '@thd-olt-component-react/fulfillment';
import { KeyProductFeatures } from '@thd-olt-component-react/key-product-features';
import {
  PodSpacer,
  ProductATC,
  ProductImage,
  ProductPod,
  ProductRating
} from '@thd-olt-component-react/product-pod';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { Price } from '@thd-olt-component-react/price';

const HeaderPriceRating = ({
  itemId,
  storeId,
  titleMaxLine = 2,
  showExactPriceOnLoad = false,
  hidePriceBadge = false,
  showDisabledATC = false,
  onSavingsCenterToggle = undefined,
  enableATC = false,
  silentATC = false,
  disabled = false,
  sharedSection = '',
  onAction = undefined,
  hideBulkPrice = false,
  hideLimitPerOrder = false,
  showPreferredPricingBadge = false,
  showPreferredPricingBadgeToolTip = false,
  hideRebates = false,
  hideSavingsText = false
}) => {
  return (
    <>
      <PodSpacer minHeight="32px">
        <ProductHeader
          brand="above"
          itemId={itemId}
          titleMaxLine={titleMaxLine}
          disableShopThisCollection
          onAction={onAction}
        />
      </PodSpacer>
      <PodSpacer minHeight="25px" padding={['BOTTOM']}>
        <ProductRating
          itemId={itemId}
        />
      </PodSpacer>
      <PodSpacer minHeight="45px" padding={['TOP']}>
        <Price
          itemId={itemId}
          large={false}
          type="simple"
          hidePromotions
          storeId={storeId}
          onSavingsCenterToggle={onSavingsCenterToggle}
          disableRangePricing={showExactPriceOnLoad}
          hideBadge={hidePriceBadge}
          showPreferredPricingBadge={showPreferredPricingBadge}
          showPreferredPricingBadgeToolTip={showPreferredPricingBadgeToolTip}
          hideBulkPrice={hideBulkPrice}
          hideLimitPerOrder={hideLimitPerOrder}
          hideRebates={hideRebates}
          hideSavingsText={hideSavingsText}
          isSignInToolTip
        />
      </PodSpacer>
      {enableATC && (
        <ProductATC
          showDisabledATC={showDisabledATC}
          disabled={disabled}
          silent={silentATC}
          sharedSection={sharedSection}
          outline
        />
      )}
    </>
  );
};

HeaderPriceRating.propTypes = {
  itemId: PropTypes.string.isRequired,
  storeId: PropTypes.string.isRequired,
  titleMaxLine: PropTypes.number,
  showExactPriceOnLoad: PropTypes.bool,
  hidePriceBadge: PropTypes.bool,
  showDisabledATC: PropTypes.bool,
  onSavingsCenterToggle: PropTypes.func,
  enableATC: PropTypes.bool,
  silentATC: PropTypes.bool,
  disabled: PropTypes.bool,
  sharedSection: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onAction: PropTypes.func,
  hideBulkPrice: PropTypes.bool,
  hideLimitPerOrder: PropTypes.bool,
  showPreferredPricingBadge: PropTypes.bool,
  showPreferredPricingBadgeToolTip: PropTypes.bool,
  hideRebates: PropTypes.bool,
  hideSavingsText: PropTypes.bool,
};

const KPFAndFulfillment = ({
  itemId,
  storeId,
  hideFulfillment = false,
  minKpfs = 3,
  showKPF = false
}) => {
  return (
    <>
      <PodSpacer padding={['TOP']}>
        {showKPF
          && (
            <KeyProductFeatures
              hideImage
              itemId={itemId}
              minfeatures={minKpfs}
              maxfeatures={5}
              simple
              oneColumnGrid
            />
          )}
      </PodSpacer>
      {!hideFulfillment && (
        <PodSpacer minHeight="80px">
          <FulfillmentPodStore itemId={itemId} storeId={storeId} hideForChooseYourOptions />
          <FulfillmentPodShipping itemId={itemId} storeId={storeId} />
        </PodSpacer>
      )}
    </>
  );
};

KPFAndFulfillment.propTypes = {
  itemId: PropTypes.string.isRequired,
  storeId: PropTypes.string.isRequired,
  hideFulfillment: PropTypes.bool,
  minKpfs: PropTypes.number,
  showKPF: PropTypes.bool,
};

export const getExperienceName = () => {
  if (!window?.experienceMetadata?.name) {
    return '';
  }

  return window?.experienceMetadata?.name;
};

export const PromotionDrawerProductPod = ({
  itemId,
  storeId,
  position,
  onArrowClick = () => {},
  callToActionSection = null,
  showKPF = false,
  showDisabledATC = false,
  hidePriceBadge = false,
  disabled = false,
  enableATC = false,
  inCartQuantity = 0,
  onAction = () => {},
  silentATC = false,
  hideCaret = false,
  sharedSection = '',
  showExactPriceOnLoad = false,
  titleMaxLine = 2,
  hideFulfillment = false,
  isHDQCSku = false,
  hideBulkPrice = false,
  hideLimitPerOrder = false,
  showPreferredPricingBadge = false,
  showPreferredPricingBadgeToolTip = false,
  hideRebates = false,
  hideSavingsText = false,
  experienceUsesProductImageCarousel = {},
  isAnchorItemId = false
}) => {
  const minKpfs = 3;
  const { channel } = useContext(ExperienceContext);
  const useCaret = hideFulfillment && !hideCaret;
  const shouldRenderCallToAction = !enableATC && callToActionSection !== null;
  const currentExperience = getExperienceName();
  const [useDualImageCarousel] = useState(
    experienceUsesProductImageCarousel[currentExperience] && (channel === 'mobile')
  );

  return (
    <div data-testid={`promotion-products-product-pod-${itemId}`}>
      <ProductPod
        itemId={itemId}
        storeId={storeId}
        analyticsData={{ parent: 'promotion-drawer', position }}
        padding="sui-pt-2"
        render={(pod = {}) => {
          const podItemId = pod.itemId || itemId;
          const podStoreId = pod.storeId || storeId;
          const podOnSavingsCenterToggle = pod.onSavingsCenterToggle || (() => {});

          return (
            <>
              <div className="sui-grid sui-grid-cols-12">
                <div className="sui-border-primary sui-p-2 sui-col-span-3">
                  <ProductImage
                    itemId={podItemId}
                    showSecondaryImage={useDualImageCarousel}
                    dualImageSwipe={useDualImageCarousel}
                    inCartQuantity={inCartQuantity}
                  />
                </div>
                <div className="sui-border-primary sui-p-2 sui-col-span-8">
                  {isAnchorItemId && (
                    <div className="sui-mb-1">
                      <Badge variant="regular" color="info" size="small">Current Item</Badge>
                    </div>
                  )}
                  <HeaderPriceRating
                    itemId={podItemId}
                    storeId={podStoreId}
                    titleMaxLine={titleMaxLine}
                    showExactPriceOnLoad={showExactPriceOnLoad}
                    hidePriceBadge={hidePriceBadge}
                    showDisabledATC={showDisabledATC}
                    onSavingsCenterToggle={podOnSavingsCenterToggle}
                    disabled={disabled}
                    enableATC={enableATC}
                    silentATC={silentATC}
                    sharedSection={sharedSection}
                    onAction={onAction}
                    hideBulkPrice={hideBulkPrice}
                    hideLimitPerOrder={hideLimitPerOrder}
                    showPreferredPricingBadge={showPreferredPricingBadge}
                    showPreferredPricingBadgeToolTip={showPreferredPricingBadgeToolTip}
                    hideRebates={hideRebates}
                    hideSavingsText={hideSavingsText}
                  />
                  <KPFAndFulfillment
                    itemId={podItemId}
                    storeId={podStoreId}
                    hideFulfillment={hideFulfillment}
                    minKpfs={minKpfs}
                    showKPF={showKPF}
                  />
                </div>
                <div className="sui-border-primary sui-p-2 sui-col-span-1">
                  {useCaret && !isHDQCSku && (
                    <IconButton
                      onClick={onArrowClick}
                      color="brand"
                      icon={ArrowForward}
                      aria-label="open menu"
                    />
                  )}
                </div>
              </div>
              {shouldRenderCallToAction && callToActionSection && (
                <PodSpacer minHeight="45px">
                  {callToActionSection}
                </PodSpacer>
              )}
            </>
          );
        }}
      />
    </div>
  );
};

PromotionDrawerProductPod.displayName = 'PromotionDrawerProductPod';

PromotionDrawerProductPod.dataModel = extend(
  Price,
  ProductHeader,
  ProductImage,
  ProductPod,
  ProductRating,
  ProductATC,
  KeyProductFeatures,
  FulfillmentPodShipping,
  FulfillmentPodStore
);

PromotionDrawerProductPod.propTypes = {
  itemId: PropTypes.string.isRequired,
  storeId: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  onArrowClick: PropTypes.func,
  callToActionSection: PropTypes.element,
  showKPF: PropTypes.bool,
  showDisabledATC: PropTypes.bool,
  hidePriceBadge: PropTypes.bool,
  disabled: PropTypes.bool,
  enableATC: PropTypes.bool,
  inCartQuantity: PropTypes.number,
  onAction: PropTypes.func,
  silentATC: PropTypes.bool,
  hideCaret: PropTypes.bool,
  sharedSection: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  showExactPriceOnLoad: PropTypes.bool,
  titleMaxLine: PropTypes.number,
  hideFulfillment: PropTypes.bool,
  isHDQCSku: PropTypes.bool,
  hideBulkPrice: PropTypes.bool,
  hideLimitPerOrder: PropTypes.bool,
  showPreferredPricingBadge: PropTypes.bool,
  showPreferredPricingBadgeToolTip: PropTypes.bool,
  hideRebates: PropTypes.bool,
  hideSavingsText: PropTypes.bool,
  experienceUsesProductImageCarousel: PropTypes.shape({}),
  isAnchorItemId: PropTypes.bool
};
